import {
  AdminOrganisationSampleRow,
  SampleMetadataLimitation,
  SampleMetadataLimitationField,
  SampleMetadataLimitationRow,
} from '@resistapp/common/api-types';
import {
  SampleSpecRow,
  normalizeSampleType,
  normalizeSamplingPointSubType,
  parseCoordinatesCell,
  parseTextCell,
} from '@resistapp/common/sample-sheet';
import {
  SampleSheetDraftRow,
  DraftValue as SharedDraftValue,
  RowValidationContext as SharedRowValidationContext,
  RowValidationIssues as SharedRowValidationIssues,
  deriveSheet,
  isRowEmpty as sharedIsRowEmpty,
  isRowReadyForSubmit as sharedIsRowReadyForSubmit,
  rowToAdminOrganisationSampleUpsertRow as sharedRowToAdminOrganisationSampleUpsertRow,
  validateRow as sharedValidateRow,
} from '@resistapp/common/sample-sheet-server';
import { SampleSubType, SampleType } from '@resistapp/common/sampling-point-types';
import { getOrgUniqueSamplingPointKey } from '@resistapp/common/sampling-points';
import { BioRep, RawSamplingPoint, SampleStatus, isPostQA } from '@resistapp/common/types';

export type DraftValue<T> = SharedDraftValue<T>;

export type SampleSheetRowDraft = SampleSheetDraftRow & {
  isSubmittedSample?: boolean;
  submittedEditsDirty?: boolean;
  hasSpecificCoordinates?: boolean;
};

export type RowValidationIssues = SharedRowValidationIssues & {
  isSubmittedSample?: string[];
  submittedEditsDirty?: string[];
  hasSpecificCoordinates?: string[];
};

export type RowValidationContext = SharedRowValidationContext;

export type SamplingPointKeyFields = Pick<
  SampleSheetRowDraft,
  'sampleDescription' | 'sampleType' | 'detailedSampleType' | 'country' | 'stateOrRegion' | 'city' | 'latitudeLongitude'
>;

export const samplingPointFieldKeys: Array<keyof SampleSheetRowDraft> = [
  'sampleDescription',
  'sampleType',
  'detailedSampleType',
  'country',
  'stateOrRegion',
  'city',
  'latitudeLongitude',
];

export const newSamplingPointSentinelLabel = 'Enter name for new sampling point';

export type NormalizedSamplingPointFields = {
  sampleDescription: string | null;
  sampleType: SampleType | null;
  detailedSampleType: SampleSubType | null;
  country: string | null;
  stateOrRegion: string | null;
  city: string | null;
  latitudeLongitude: SampleSpecRow['latitudeLongitude'];
};

export type SamplingPointSamplingPointData = NormalizedSamplingPointFields & {
  samplingPointId: number;
};

export type SamplingPointDraftState = 'existing' | 'new' | 'new-shared';

function generateRowId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `row-${crypto.randomUUID()}`;
  }
  return `row-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function createEmptySampleSheetRow(): SampleSheetRowDraft {
  return {
    rowId: generateRowId(),
    status: SampleStatus.SUBMITTED,
    samplingPointId: null,
    isReadOnly: false,
    sampleId: null,
    sampleNumber: null,
    bioReplica: null,
    sampleDescription: null,
    sampleType: null,
    detailedSampleType: null,
    flowRate: null,
    operatedFlowRate: null,
    suspendedSolids: null,
    bod: null,
    filteredVolume: null,
    primElutedDnaVolume: null,
    primElutedDnaConcentration: null,
    primElutedDnaQuality280: null,
    primElutedDnaQuality230: null,
    primDilutedDnaConcentration: null,
    primDilutedDnaQuality280: null,
    primDilutedDnaQuality230: null,
    primDilutedDnaVolume: null,
    primDnaExtractionKit: null,
    sampleCollectionDate: null,
    city: null,
    stateOrRegion: null,
    country: null,
    latitudeLongitude: null,
  } satisfies SampleSheetRowDraft;
}

export function resolveFirstSampleNumber(args: {
  workbookFirstSampleNumber?: number | null;
  sheetMeta?: Record<string, unknown> | null;
}): number {
  const workbookFirstSampleNumber = normalizePositiveInteger(args.workbookFirstSampleNumber);
  if (workbookFirstSampleNumber != null) {
    return workbookFirstSampleNumber;
  }

  const metaFirstSampleNumber = readSheetMetaFirstSampleNumber(args.sheetMeta);
  if (metaFirstSampleNumber != null) {
    return metaFirstSampleNumber;
  }

  return 1;
}

export function formatMetadataLimitationSampleLabel(
  row: Pick<SampleMetadataLimitationRow, 'sampleNumber' | 'bioReplica' | 'sampleDescription'>,
  rowIndex: number,
): string {
  const bioReplica = row.bioReplica?.trim().toUpperCase();
  if (row.sampleNumber != null) {
    return `Sample ${row.sampleNumber}${bioReplica ? ` ${bioReplica}` : ''}`;
  }
  return row.sampleDescription ?? `Selected row ${rowIndex + 1}`;
}

const metadataLimitationCategoryOrder = ['volume_normalisation', 'sample_volume', 'sample_quality'] as const;
const maxListedLimitationSamples = 6;

export type MetadataLimitationCategory = SampleMetadataLimitation['category'];

export type MetadataLimitationFieldSummary = {
  field: SampleMetadataLimitationField;
  sampleText: string;
};

export type MetadataLimitationCategorySummary = {
  category: MetadataLimitationCategory;
  fields: MetadataLimitationFieldSummary[];
  // Highest recommended minimum across the affected samples, so the dialog can name the volume the lab needs.
  recommendedMinimum: number | null;
};

// Groups the server's per-row limitations by consequence, so each explanation can sit next to the samples it concerns.
export function summariseMetadataLimitations(
  rows: readonly SampleMetadataLimitationRow[],
): MetadataLimitationCategorySummary[] {
  const samplesByCategoryAndField = new Map<string, string[]>();
  const recommendedMinimumByCategory = new Map<MetadataLimitationCategory, number>();

  rows.forEach((row, rowIndex) => {
    const sampleLabel = formatMetadataLimitationSampleLabel(row, rowIndex);
    row.limitations.forEach(limitation => {
      const key = `${limitation.category}|${limitation.field}`;
      const labels = samplesByCategoryAndField.get(key) ?? [];
      labels.push(formatLimitationSampleLabel(sampleLabel, limitation));
      samplesByCategoryAndField.set(key, labels);
      if (limitation.recommendedMinimum != null) {
        const current = recommendedMinimumByCategory.get(limitation.category);
        recommendedMinimumByCategory.set(limitation.category, Math.max(current ?? 0, limitation.recommendedMinimum));
      }
    });
  });

  return metadataLimitationCategoryOrder.flatMap(category => {
    const fields = Array.from(samplesByCategoryAndField.entries())
      .filter(([key]) => key.startsWith(`${category}|`))
      .map(([key, sampleLabels]) => ({
        field: key.slice(category.length + 1) as SampleMetadataLimitationField,
        sampleText: formatLimitationSampleList(sampleLabels),
      }));
    return fields.length === 0
      ? []
      : [{ category, fields, recommendedMinimum: recommendedMinimumByCategory.get(category) ?? null }];
  });
}

function formatLimitationSampleLabel(sampleLabel: string, limitation: SampleMetadataLimitation): string {
  return limitation.reason === 'below_recommended_minimum' && limitation.value != null
    ? `${sampleLabel} (${limitation.value} µL)`
    : sampleLabel;
}

function formatLimitationSampleList(sampleLabels: readonly string[]): string {
  const uniqueSampleLabels = Array.from(new Set(sampleLabels));
  const visibleSampleLabels = uniqueSampleLabels.slice(0, maxListedLimitationSamples);
  const remainingCount = uniqueSampleLabels.length - visibleSampleLabels.length;
  return `${visibleSampleLabels.join(', ')}${remainingCount > 0 ? `, and ${remainingCount} more` : ''}`;
}

// The websocket republishes this on every change, so it is the signal that the cached workbook summary went stale.
export function readSheetMetaFirstSampleNumber(sheetMeta?: Record<string, unknown> | null): number | null {
  return normalizePositiveInteger(sheetMeta?.firstSampleNumber);
}

// The input keeps whatever the operator typed, so `01` and `1.0` are unsaved-looking spellings of an already-saved
// value. Comparing the parsed numbers instead leaves nothing to save, and therefore nothing blocking submission.
// Invalid input still counts as edited so that submission waits until the operator resolves it.
export function isFirstSampleNumberEdited(args: {
  input: string;
  configuredFirstSampleNumber: number;
  hasValidationError: boolean;
}): boolean {
  if (args.hasValidationError) {
    return true;
  }
  return Number(args.input.trim()) !== args.configuredFirstSampleNumber;
}

// Everyone who confirms numbering works on the analysing side, but only some of them can still change it: the First
// sample control needs administrator rights and a workbook with nothing submitted yet. Each variant therefore names a
// remedy its reader can actually perform.
export type SampleNumberingContinuationState = 'can-change' | 'locked' | 'set-elsewhere';

export function resolveSampleNumberingContinuationState(args: {
  hasSubmittedSamples: boolean;
  canConfigureFirstSampleNumber: boolean;
}): SampleNumberingContinuationState {
  if (args.hasSubmittedSamples) {
    return 'locked';
  }
  return args.canConfigureFirstSampleNumber ? 'can-change' : 'set-elsewhere';
}

export function getSampleNumberingContinuationMessage(state: SampleNumberingContinuationState): string {
  switch (state) {
    case 'can-change':
      return 'If these samples continue an existing project, cancel and set First sample to the next intended number before submitting.';
    case 'locked':
      return 'This workbook already has submitted samples, so numbering continues from them and First sample can no longer be changed here. If the range above is wrong, cancel and correct the existing samples before submitting more.';
    case 'set-elsewhere':
      return 'First sample was set when this workbook was created and cannot be changed here. If the range above is wrong, cancel and check with whoever set the workbook up before submitting.';
  }
}

// Mirrors `deriveSheet`, which locks a row's numbering as soon as it carries a sampleId. A reset workbook keeps the
// sampleId on editable rows so they are resubmitted into the same sample records, so previewing them as fresh drafts
// would show numbers the server will never create.
export function hasServerLockedNumbering(
  row: Pick<SampleSheetRowDraft, 'isSubmittedSample' | 'isReadOnly' | 'sampleId'> | null | undefined,
): boolean {
  if (isSubmittedSampleLikeRow(row)) {
    return true;
  }
  return typeof row?.sampleId === 'number' && Number.isFinite(row.sampleId);
}

export function isSubmittedSampleLikeRow(
  row: Pick<SampleSheetRowDraft, 'isSubmittedSample' | 'isReadOnly' | 'sampleId'> | null | undefined,
): boolean {
  if (row?.isSubmittedSample) {
    return true;
  }
  return Boolean(row?.isReadOnly) && typeof row?.sampleId === 'number' && Number.isFinite(row.sampleId);
}

const bioRepValues = new Set(Object.values(BioRep));
const sampleStatusValues = new Set(Object.values(SampleStatus));

function normalizePositiveInteger(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || !Number.isInteger(value) || value <= 0) {
    return null;
  }
  return value;
}

function toBioRep(value: string | null | undefined): BioRep | null {
  if (!value) {
    return null;
  }
  return bioRepValues.has(value as BioRep) ? (value as BioRep) : null;
}

function toSampleStatus(value: string | null | undefined): SampleStatus | null {
  if (!value) {
    return null;
  }
  return sampleStatusValues.has(value as SampleStatus) ? (value as SampleStatus) : null;
}

function cloneCoordinates(
  value: AdminOrganisationSampleRow['latitudeLongitude'],
): SampleSpecRow['latitudeLongitude'] | null {
  if (!value) {
    return null;
  }
  const { lat, lon } = value;
  return typeof lat === 'number' && typeof lon === 'number' ? { lat, lon } : null;
}

export function createSampleSheetRowFromExistingSample(sample: AdminOrganisationSampleRow): SampleSheetRowDraft {
  const status = toSampleStatus(sample.status);
  return {
    rowId: `existing-${sample.sampleId}`,
    samplingPointId: typeof sample.samplingPointId === 'number' ? sample.samplingPointId : null,
    status,
    isReadOnly: status != null && isPostQA(status),
    sampleId: sample.sampleId,
    sampleNumber: sample.sampleNumber ?? null,
    bioReplica: toBioRep(sample.bioReplica),
    sampleDescription: sample.sampleDescription ?? null,
    sampleType: sample.sampleType ?? null,
    detailedSampleType: sample.detailedSampleType ?? null,
    flowRate: sample.flowRate ?? null,
    operatedFlowRate: sample.operatedFlowRate ?? null,
    suspendedSolids: sample.suspendedSolids ?? null,
    bod: sample.bod ?? null,
    filteredVolume: sample.filteredVolume ?? null,
    primElutedDnaVolume: sample.primElutedDnaVolume ?? null,
    primElutedDnaConcentration: sample.primElutedDnaConcentration ?? null,
    primElutedDnaQuality280: sample.primElutedDnaQuality280 ?? null,
    primElutedDnaQuality230: sample.primElutedDnaQuality230 ?? null,
    primDilutedDnaVolume: sample.primDilutedDnaVolume ?? null,
    primDilutedDnaConcentration: sample.primDilutedDnaConcentration ?? null,
    primDilutedDnaQuality280: sample.primDilutedDnaQuality280 ?? null,
    primDilutedDnaQuality230: sample.primDilutedDnaQuality230 ?? null,
    primDnaExtractionKit: sample.primDnaExtractionKit ?? null,
    sampleCollectionDate: sample.sampleCollectionDate ?? null,
    city: sample.city ?? null,
    stateOrRegion: sample.stateOrRegion ?? null,
    country: sample.country ?? null,
    latitudeLongitude: cloneCoordinates(sample.latitudeLongitude),
  } satisfies SampleSheetRowDraft;
}
export function applyDerivedSampleSheetValues(rows: SampleSheetRowDraft[]): SampleSheetRowDraft[] {
  return deriveSheet(rows);
}

export function cloneRow(row: SampleSheetRowDraft): SampleSheetRowDraft {
  const coords = row.latitudeLongitude;
  return {
    ...row,
    sampleCollectionDate:
      row.sampleCollectionDate instanceof Date ? new Date(row.sampleCollectionDate) : row.sampleCollectionDate,
    latitudeLongitude: typeof coords === 'string' ? coords : coords ? { ...coords } : null,
  };
}

export function replaceRowValue<K extends keyof SampleSheetRowDraft>(
  row: SampleSheetRowDraft,
  key: K,
  value: SampleSheetRowDraft[K],
): SampleSheetRowDraft {
  return {
    ...row,
    [key]: value,
  };
}

export function setRowId(row: SampleSheetRowDraft, rowId: string): SampleSheetRowDraft {
  return {
    ...row,
    rowId,
  };
}

export function collectRowValidationIssues(
  row: SampleSheetRowDraft,
  context: RowValidationContext = {},
): RowValidationIssues {
  return sharedValidateRow(row, context);
}

export function isRowReadyForSubmit(row: SampleSheetRowDraft, context: RowValidationContext = {}): boolean {
  return sharedIsRowReadyForSubmit(row, context);
}

export const rowToAdminOrganisationSampleUpsertRow = sharedRowToAdminOrganisationSampleUpsertRow;

export const isRowEmpty = sharedIsRowEmpty;

export function normalizeSamplingPointFields(
  fields: SamplingPointKeyFields,
): NormalizedSamplingPointFields {
  const sampleDescriptionRaw = parseTextCell(fields.sampleDescription) ?? null;
  const sampleDescription = sampleDescriptionRaw ? sampleDescriptionRaw.replace(/\s+/g, ' ').trim() : null;
  const normalizedType = normalizeSampleType(fields.sampleType);
  const sampleType = typeof normalizedType === 'undefined' ? null : normalizedType;
  const normalizedSubtype = normalizeSamplingPointSubType(fields.detailedSampleType, sampleType ?? null);
  const detailedSampleType = typeof normalizedSubtype === 'undefined' ? null : normalizedSubtype ?? null;
  const country = parseTextCell(fields.country) ?? null;
  const stateOrRegion = parseTextCell(fields.stateOrRegion) ?? null;
  const city = parseTextCell(fields.city) ?? null;
  const normalizedCoordinates = parseCoordinatesCell(fields.latitudeLongitude);
  const latitudeLongitude =
    normalizedCoordinates === undefined ? null : normalizedCoordinates ? { ...normalizedCoordinates } : null;

  return {
    sampleDescription,
    sampleType,
    detailedSampleType,
    country,
    stateOrRegion,
    city,
    latitudeLongitude,
  } satisfies NormalizedSamplingPointFields;
}

export function buildSamplingPointUniqueKey(
  fields: SamplingPointKeyFields,
): string | null {
  const normalized = normalizeSamplingPointFields(fields);
  if (!normalized.sampleDescription || !normalized.sampleType) {
    return null;
  }

  const rawSamplingPoint: RawSamplingPoint = {
    name: normalized.sampleDescription,
    type: normalized.sampleType,
    subtype: normalized.detailedSampleType ?? null,
    country: normalized.country,
    region: normalized.stateOrRegion,
    city: normalized.city,
    lat: normalized.latitudeLongitude?.lat ?? null,
    lon: normalized.latitudeLongitude?.lon ?? null,
    inferredLat: null,
    inferredLon: null,
    adminLevels: null,
  };

  return getOrgUniqueSamplingPointKey(rawSamplingPoint);
}

export function computeSamplingPointDraftStates(
  rows: readonly SampleSheetRowDraft[],
): Map<string, SamplingPointDraftState> {
  if (rows.length === 0) {
    return new Map();
  }

  const uniqueKeyByRowId = new Map<string, string | null>();
  const draftKeyCounts = new Map<string, number>();

  rows.forEach(row => {
    const rowId = row.rowId;
    if (typeof rowId !== 'string' || rowId === '') {
      return;
    }
    if (row.samplingPointId != null) {
      uniqueKeyByRowId.set(rowId, null);
      return;
    }
    const key = buildSamplingPointUniqueKey(row);
    uniqueKeyByRowId.set(rowId, key);
    if (!key) {
      return;
    }
    draftKeyCounts.set(key, (draftKeyCounts.get(key) ?? 0) + 1);
  });

  const states = new Map<string, SamplingPointDraftState>();

  rows.forEach(row => {
    const rowId = row.rowId;
    if (typeof rowId !== 'string' || rowId === '') {
      return;
    }
    if (row.samplingPointId != null) {
      states.set(rowId, 'existing');
      return;
    }
    const key = uniqueKeyByRowId.get(rowId);
    if (key && (draftKeyCounts.get(key) ?? 0) > 1) {
      states.set(rowId, 'new-shared');
      return;
    }
    states.set(rowId, 'new');
  });

  return states;
}

export function samplingPointFieldsEqual(
  fields: SamplingPointKeyFields,
  samplingPoint: SamplingPointSamplingPointData,
): boolean {
  const normalizedLeft = normalizeSamplingPointFields(fields);
  const normalizedRight = normalizeSamplingPointFields(samplingPoint);

  if (normalizedLeft.sampleDescription !== normalizedRight.sampleDescription) {
    return false;
  }
  if (normalizedLeft.sampleType !== normalizedRight.sampleType) {
    return false;
  }
  if (normalizedLeft.detailedSampleType !== normalizedRight.detailedSampleType) {
    return false;
  }
  if (normalizedLeft.country !== normalizedRight.country) {
    return false;
  }
  if (normalizedLeft.stateOrRegion !== normalizedRight.stateOrRegion) {
    return false;
  }
  if (normalizedLeft.city !== normalizedRight.city) {
    return false;
  }
  return coordinatesAreEqual(normalizedLeft.latitudeLongitude, normalizedRight.latitudeLongitude);
}

export function applySamplingPointFieldsToRow(
  row: SampleSheetRowDraft,
  fields: NormalizedSamplingPointFields,
  samplingPointId: number | null,
): SampleSheetRowDraft {
  const normalized = normalizeSamplingPointFields(fields);
  const latitudeLongitude = normalized.latitudeLongitude ? { ...normalized.latitudeLongitude } : null;

  return {
    ...row,
    samplingPointId,
    sampleDescription: normalized.sampleDescription,
    sampleType: normalized.sampleType,
    detailedSampleType: normalized.detailedSampleType,
    country: normalized.country,
    stateOrRegion: normalized.stateOrRegion,
    city: normalized.city,
    latitudeLongitude,
  } satisfies SampleSheetRowDraft;
}

export function applySamplingPointToRow(
  row: SampleSheetRowDraft,
  samplingPoint: SamplingPointSamplingPointData,
): SampleSheetRowDraft {
  const alreadyMatching =
    row.samplingPointId === samplingPoint.samplingPointId && samplingPointFieldsEqual(row, samplingPoint);
  if (alreadyMatching) {
    return row;
  }

  return applySamplingPointFieldsToRow(row, samplingPoint, samplingPoint.samplingPointId);
}

function coordinatesAreEqual(
  left: SampleSpecRow['latitudeLongitude'],
  right: SampleSpecRow['latitudeLongitude'],
): boolean {
  if (!left && !right) {
    return true;
  }
  if (!left || !right) {
    return false;
  }
  return left.lat === right.lat && left.lon === right.lon;
}
